<template>
  <div class="overflow-x-auto scrollbar-hidden">
    <div id="tabulator" ref="tableRef" class="mt-5 table-report table-report--tabulator"></div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { TabulatorFull as Tabulator } from 'tabulator-tables'
import langs from '@/locale.tabulator'
import superadminApi from '@/services/superadmin/superadmin.api'
import Litepicker from 'litepicker'

export default defineComponent({
  data() {
    return {
      tabulator: null,
      dateFilter: null
    }
  },
  methods: {
    showDetail(e, cell) {
      this.$router.push({
        name: 'superadmin-tools-sign-up-detail',
        params: { uuid: cell.getData().uuid }
      })
    },
    showEdit(e, cell) {
      this.$router.push({
        name: 'superadmin-tools-sign-up-edit',
        params: { uuid: cell.getData().uuid }
      })
    },
    detailFormatter(cell) {
      return (
        '<div class="flex lg:justify-center items-center"><button class="btn btn-info detail-action">' +
        this.$t('common.detail') +
        '</button></div>'
      )
    },
    editFormatter(cell) {
      if (cell.getData().processed) {
        return
      }
      return (
        '<div class="flex lg:justify-center items-center"><button class="btn btn-primary detail-action">' +
        this.$t('common.edit') +
        '</button></div>'
      )
    },
    processedFormatter(cell) {
      if (cell.getData().processed) {
        return (
          '<div><div class="font-medium whitespace-nowrap">' +
          (cell.getData().deleted === null ? this.$t('common.approved') : this.$t('common.rejected')) +
          '</div></div>'
        )
      }
      return (
        '<div><div class="font-medium whitespace-nowrap">' +
        this.$t('common.no') +
        '</div></div>'
      )
    },
    createdFilter(cell) {
      const input = document.createElement('input')
      new Litepicker({
        element: input,
        singleMode: true,
        format: 'DD.MM.YYYY',
        resetButton: true,
        setup: picker => {
          picker.on('selected', (startDate, endDate) => {
            if (this.dateFilter !== null) {
              cell.getColumn().getTable().removeFilter('created', '=', this.dateFilter)
            }
            this.dateFilter = startDate.toJSDate()
            this.dateFilter.setHours(10)
            this.dateFilter = this.dateFilter.toISOString()
            cell.getColumn().getTable().addFilter('created', '=', this.dateFilter)
          })
          picker.on('clear:selection', () => {
            if (this.dateFilter !== null) {
              cell.getColumn().getTable().removeFilter('created', '=', this.dateFilter)
            }
          })
        }
      })
      return input
    },
    createdFormatter(cell) {
      return (
        '<div><div class="font-medium whitespace-nowrap">' +
        this.formatDate(cell.getData().created) +
        '</div></div>'
      )
    },
    formatDate(date) {
      const d = new Date(date)
      return (
        d.toISOString().replace(/^(\d+)-(\d+)-(\d+).*$/, '$3.$2.$1') +
        ' ' +
        d.toLocaleTimeString().replace(/^(\d+):(\d+):\d+$/, '$1:$2')
      )
    }
  },
  mounted() {
    this.tabulator = new Tabulator(this.$refs.tableRef, {
      locale: 'sk',
      langs: langs,
      ajaxURL: '#',
      ajaxConfig: 'POST',
      ajaxRequestFunc: function (url, config, params) {
        return superadminApi.getSignUpList(params.page, params.size, params.filter)
      },
      ajaxResponse: function (url, params, response) {
        const pagination = response.data.pagination
        return {
          data: response.data.data,
          last_page: Math.ceil(pagination.filtered / pagination.perPage)
        }
      },
      printAsHtml: true,
      printStyled: true,
      pagination: true,
      paginationMode: 'remote',
      paginationSize: 10,
      paginationSizeSelector: [10, 20, 30, 40],
      sortMode: 'remote',
      filterMode: 'remote',
      layout: 'fitColumns',
      responsiveLayout: 'collapse',
      placeholder: 'No matching records found',
      columns: [
        {
          formatter: 'responsiveCollapse',
          width: 40,
          minWidth: 30,
          hozAlign: 'center',
          resizable: false,
          headerSort: false
        },

        // For HTML table
        {
          title: this.$t('common.title'),
          responsive: 0,
          field: 'name',
          vertAlign: 'middle',
          headerSort: false,
          print: false,
          download: false,
          headerFilter: 'input',
          formatter(cell) {
            return `<div>
                  <div class="font-medium whitespace-nowrap">${cell.getData().businessName
              }</div>
                </div>`
          }
        },
        {
          title: 'Email',
          responsive: 0,
          field: 'email',
          vertAlign: 'middle',
          headerHozAlign: 'left',
          headerSort: false,
          print: false,
          download: false,
          headerFilter: 'input',
          formatter(cell) {
            return `<div>
                  <div class="font-medium whitespace-nowrap">${cell.getData().email}</div>
                </div>`
          }
        },
        {
          title: this.$t('common.phoneNumber'),
          responsive: 0,
          field: 'phoneNumber',
          vertAlign: 'middle',
          headerHozAlign: 'left',
          headerSort: false,
          print: false,
          download: false,
          headerFilter: 'input',
          formatter(cell) {
            return `<div>
                  <div class="font-medium whitespace-nowrap">${cell.getData().phoneNumber
              }</div>
                </div>`
          }
        },
        {
          title: this.$t('common.created'),
          responsive: 0,
          field: 'created',
          vertAlign: 'middle',
          headerHozAlign: 'left',
          headerSort: false,
          print: false,
          download: false,
          headerFilter: this.createdFilter,
          formatter: this.createdFormatter
        },
        {
          title: this.$t('common.processed'),
          responsive: 0,
          field: 'created',
          vertAlign: 'middle',
          headerSort: false,
          print: false,
          download: false,
          formatter: this.processedFormatter
        },
        {
          title: '',
          maxWidth: 150,
          field: 'actionDetail',
          responsive: 1,
          headerSort: false,
          hozAlign: 'center',
          vertAlign: 'middle',
          print: false,
          download: false,
          formatter: this.detailFormatter,
          cellClick: this.showDetail
        },
        {
          title: '',
          maxWidth: 150,
          field: 'actionEdit',
          responsive: 1,
          headerSort: false,
          hozAlign: 'center',
          vertAlign: 'middle',
          print: false,
          download: false,
          formatter: this.editFormatter,
          cellClick: this.showEdit
        }
      ]
    })
  }
})
</script>
